<template>
  <div
    id="side-menu"
    class="side-menu"
    :style="{
      maxHeight: `calc(100vh - ${
        offsetTop < 120 ? 200 - offsetTop : 60
      }px)`,
    }"
  >
    <div
      v-for="listTitles in listArticlesSearch"
      :key="listTitles.id"
      class="article"
      :style="{
        ...(!blockVisible[listTitles.linkName] && {
          marginBottom: '10px',
        }),
      }"
    >
      <div
        class="title"
        @click="scrollIntoAnchor(listTitles.linkName, false)"
      >
        {{ $t(listTitles.title) }}
        <SvgHelpCenterMinusSvg
          class="toggle"
          v-if="blockVisible[listTitles.linkName]"
        />
        <SvgHelpCenterPlusSvg class="toggle__plus" v-else />
      </div>
      <div
        v-for="listSubTitles in listTitles.articlesTitle"
        :key="listSubTitles.id"
        class="sub-title"
        v-show="blockVisible[listTitles.linkName]"
      >
        <ui-link
          :to="`${listTitles.linkName}?article=${listSubTitles.articles[0].title}`"
          @click.stop="scrollIntoAnchor"
          >{{ $t(listSubTitles.title) }}</ui-link
        >
        <div
          class="article-text"
          :class="{
            'article-text_active': activeLink === article.title,
          }"
          v-for="article in listSubTitles.articles"
          :key="article.id"
        >
          <ui-link
            :to="`${listTitles.linkName}?article=${article.title}`"
            @click.stop="scrollIntoAnchor"
          >
            {{ $t(article.title) }}
          </ui-link>
        </div>
      </div>
    </div>
  </div>
  <div class="side-menu_mobile">
    <div
      v-if="offsetTop > 60 || mobileNavMenu"
      class="mobile-navigation-menu-button"
      @click="showMobileMenu"
    >
      <div class="mobile-navigation-menu-button__dots" />
      <div class="mobile-navigation-menu-button__dots" />
      <div class="mobile-navigation-menu-button__dots" />
    </div>

    <div class="wrapper" v-if="mobileNavMenu">
      <div
        v-for="listTitles in listArticlesSearch"
        :key="listTitles.id"
        class="article"
        :style="{
          ...(!blockVisible[listTitles.linkName] && {
            marginBottom: '10px',
          }),
        }"
      >
        <div
          class="title"
          @click="scrollIntoAnchorMobile(listTitles.linkName, false)"
        >
          {{ $t(listTitles.title) }}
          <SvgHelpCenterMinusSvg
            class="toggle"
            v-if="blockVisible[listTitles.linkName]"
          />
          <SvgHelpCenterPlusSvg class="toggle__plus" v-else />
        </div>
        <div
          v-for="listSubTitles in listTitles.articlesTitle"
          :key="listSubTitles.id"
          class="sub-title"
          v-show="blockVisible[listTitles.linkName]"
        >
          <ui-link
            :to="`${listTitles.linkName}?article=${listSubTitles.articles[0].title}`"
            @click.stop="scrollIntoAnchorMobile"
            >{{ $t(listSubTitles.title) }}</ui-link
          >
          <div
            class="article-text"
            :class="{
              'article-text_active': activeLink === article.title,
            }"
            v-for="article in listSubTitles.articles"
            :key="article.id"
          >
            <ui-link
              :to="`${listTitles.linkName}?article=${article.title}`"
              @click.stop="scrollIntoAnchorMobile"
            >
              {{ $t(article.title) }}
            </ui-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {TIntersectionObserver} from '~/interfaces';
import {useStaticStore} from '~/store/static.store';

const {listArticles} = useStaticStore();
const router = useRouter();
const listArticlesSearch = ref(listArticles);
const isScrollToAnchor = ref(false);
const blockVisible = ref({});
const mobileNavMenu = ref(false);
const refTimeout = ref<NodeJS.Timeout | null>(null);

const showMobileMenu = () => {
  mobileNavMenu.value = !mobileNavMenu.value;
};

const scrollIntoAnchorMobile = (
  activeItem?: string,
  isNeedScroll = true,
) => {
  if (typeof activeItem === 'string') {
    blockVisible.value = {
      [activeItem]: !blockVisible.value[activeItem],
    };
  }
  if (isNeedScroll) {
    isScrollToAnchor.value = true;
    setTimeout(() => {
      const currentPathAnchor = document.getElementById(
        `${router.currentRoute.value.query.article}`,
      );

      if (currentPathAnchor) {
        currentPathAnchor?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        if (refTimeout.value) {
          clearTimeout(refTimeout.value);
          refTimeout.value = null;
        }
        refTimeout.value = setTimeout(() => {
          isScrollToAnchor.value = false;
        }, 1000);
      }
    }, 10);
    showMobileMenu();
  }
};

const scrollIntoAnchor = (
  activeItem?: string,
  isNeedScroll = true,
) => {
  if (typeof activeItem === 'string') {
    blockVisible.value = {
      [activeItem]: !blockVisible.value[activeItem],
    };
  }
  if (isNeedScroll) {
    isScrollToAnchor.value = true;
    setTimeout(() => {
      const currentPathAnchor = document.getElementById(
        `${router.currentRoute.value.query.article}`,
      );

      if (currentPathAnchor) {
        currentPathAnchor?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        if (refTimeout.value) {
          clearTimeout(refTimeout.value);
          refTimeout.value = null;
        }
        refTimeout.value = setTimeout(() => {
          isScrollToAnchor.value = false;
        }, 1000);
      }
    }, 10);
  }
};

const offsetTop = ref(0);

const activeLink = computed(() => {
  return router.currentRoute.value.query?.article;
});

const changeSideMaxHeight = () => {
  if (typeof document !== 'undefined') {
    const sideMenuBlockOffsetTop =
      document.getElementById('side-menu')?.offsetTop || 0;
    offsetTop.value = window.pageYOffset;
    return;
  }
};

const callbackObserver = function (entries: TIntersectionObserver) {
  if (entries[0].isIntersecting && !isScrollToAnchor.value) {
    router.replace({query: {article: entries[0].target.id}});
  }
};

onMounted(() => {
  setTimeout(() => {
    scrollIntoAnchor(
      router.currentRoute.value.path.split('center/')[1],
    );
  }, 300);
  document.addEventListener('scroll', changeSideMaxHeight);

  setTimeout(() => {
    const observer = new IntersectionObserver(callbackObserver);

    [
      'howWhyUse',
      'dreamFlatFunctionality',
      'finResult',
      'uEngine5Graphics',
      'systemRequirement',
      'installingRunning',
      'subscriptionPlans',
      'subManagement',
      'howToSwitch',
      'menu',
      'creatingRoomLayout',
      'saveRoomLayoutWithoutNumber',
      'savingEditingExistingLayout',
      'switchInteriorDesmode',
      'cameraModeSettings',
      'settingsGeneralEtc',
      'helpControl',
      'notes',
      'roulette',
      'movingObjectsThroughWalls',
      'leftBrowserSections',
      'favorites',
      'search',
      'finishingWord',
      'modelsAndTheirFunctions',
      'furnitDecorBathEquipOther',
      'decorHeadWithoutNumber',
      'lighting',
      'kitchen',
      'primitives',
      'customObjectsWithoutNumber',
      'doorsAndWindows',
      'workingWithObjects',
      'savingDes',
      'replacingDesignVersion',
      'reOpeningDesign',
      'publicationPublicDomain',
      'renderers',
      'videoFlyThroughs',
      'tours3DWeb',
      'interactiveTours',
      'vr',
    ].forEach((item) => {
      const target = document.querySelector(`#${item}`);

      if (target) {
        observer.observe(target);
      }
    });
  }, 500);
});

onUnmounted(() => {
  document.removeEventListener('scroll', changeSideMaxHeight);
});
</script>

<style scoped lang="scss">
@import 'css/help-center/help-center-general-ctyle';
.side-menu_mobile {
  .wrapper {
    position: fixed;
    left: 0px;
    top: 0px;
    width: 100vw;
    height: 100vh;
    background: var(--var-white-000);
    padding: $p-3;
    z-index: 10000;
    overflow-y: auto;

    .article {
      margin-bottom: $mt-13;
      .title {
        position: relative;
        @include subtitle-fs-18-fw-700-black-100;
        cursor: pointer;
        margin-bottom: $m-5;

        svg {
          margin-left: 10px;
        }
      }

      .sub-title {
        @include launcher-h1;
        color: var(--var-black-200);
        margin-top: $m-6;

        .article-text {
          @include launcher-p1;
          margin-top: $m-3;

          &_active {
            @include launcher-p1;
            color: var(--var-blue-100);
            background: var(--var-blue-500);
            padding: $p-2 $p-4;
            border-radius: $br-2;
          }
        }
      }
    }
  }
  @media (min-width: $lg) {
    display: none;
  }
}
.side-menu {
  position: sticky;
  top: 30px;
  width: 100%;
  max-width: 300px;
  border-left: 1px solid var(--var-white-400);
  padding: $p-0 $p-4;
  margin-top: $mt-5;
  margin-bottom: $mt-5;
  overflow-y: auto;
  max-height: 80vh;

  .article {
    margin-bottom: $mt-13;
    .title {
      position: relative;
      @include subtitle-fs-18-fw-700-black-100;
      cursor: pointer;
      margin-bottom: $m-5;

      svg {
        margin-left: 10px;
      }
    }

    .sub-title {
      @include launcher-h1;
      color: var(--var-black-200);
      margin-top: $m-6;

      .article-text {
        @include launcher-p1;
        margin-top: $m-3;

        &_active {
          @include launcher-p1;
          color: var(--var-blue-100);
          background: var(--var-blue-500);
          padding: $p-2 $p-4;
          border-radius: $br-2;
        }
      }
    }
  }

  @media (max-width: $lg) {
    display: none;
  }
}
</style>
